// The one place the check-in frequencies are named, so the wizard's radio
// buttons and the check-in page's cadence text can never drift apart.

// Frequency values as Custom-Api's CheckInFrequency enum defines them; only the
// wording changes here, never the numbers we send.
export const CHECK_IN_FREQUENCY = {
  daily: 1,
  weekly: 2,
  fortnightly: 3,
  everyThreeWeeks: 4,
  everyFourWeeks: 5,
}

export const CHECK_IN_FREQUENCY_OPTIONS = [
  {value: CHECK_IN_FREQUENCY.daily, label: 'Daily'},
  {value: CHECK_IN_FREQUENCY.weekly, label: 'Weekly'},
  {value: CHECK_IN_FREQUENCY.fortnightly, label: 'Fortnightly'},
  {value: CHECK_IN_FREQUENCY.everyThreeWeeks, label: 'Every 3 weeks'},
  {value: CHECK_IN_FREQUENCY.everyFourWeeks, label: 'Every 4 weeks'},
]

const LABEL_BY_FREQUENCY = Object.fromEntries(
  CHECK_IN_FREQUENCY_OPTIONS.map(({value, label}) => [value, label])
)

// The stored frequency arrives as a number from the API and as a string from the
// wizard's radio group, so both have to name the same cadence.
export const frequencyLabel = frequency =>
  LABEL_BY_FREQUENCY[Number(frequency)] ?? ''

const CADENCE_BY_FREQUENCY = {
  [CHECK_IN_FREQUENCY.daily]: 'daily',
  [CHECK_IN_FREQUENCY.weekly]: 'weekly',
  [CHECK_IN_FREQUENCY.fortnightly]: 'fortnightly',
  [CHECK_IN_FREQUENCY.everyThreeWeeks]: 'every 3 weeks',
  [CHECK_IN_FREQUENCY.everyFourWeeks]: 'every 4 weeks',
}

// A frequency we do not recognise used to fall through to "4 Weeks", which told
// the school something untrue; saying nothing specific is the safer answer.
export const launchCadenceSentence = frequency => {
  const cadence = CADENCE_BY_FREQUENCY[Number(frequency)]
  return cadence
    ? `A new check-in survey launches ${cadence}.`
    : 'A new check-in survey launches on the schedule you set.'
}

// How long one pass through the question pool takes, in check-in surveys. The
// count comes from the API: the instances actually generated once the check-in
// is running, or the projection from the chosen templates before then. It used
// to be the number of templates, which is not the same thing at all - two
// templates totalling 14 questions make three surveys, not two.
export const cycleLengthSentence = instanceCount => {
  const count = Number(instanceCount)
  if (!(count > 0)) {
    return 'The full suite of check-in questions is completed once every survey in the suite has launched.'
  }
  return `The full suite of check-in questions is completed every ${count} check-in ${
    count === 1 ? 'survey' : 'surveys'
  }.`
}

// The date is already formatted for display; a holiday or a pause can still
// push it later, which is why it is only ever "expected".
export const cycleCompletionSentence = formattedEndDate =>
  formattedEndDate
    ? `This check-in cycle is expected to complete on ${formattedEndDate}.`
    : ''

// Days from one launch to the next, as Custom-Api's CheckInInstanceWindow
// counts them. Only used to project dates before the API has laid any out.
export const DAYS_PER_CYCLE = {
  [CHECK_IN_FREQUENCY.daily]: 1,
  [CHECK_IN_FREQUENCY.weekly]: 7,
  [CHECK_IN_FREQUENCY.fortnightly]: 14,
  [CHECK_IN_FREQUENCY.everyThreeWeeks]: 21,
  [CHECK_IN_FREQUENCY.everyFourWeeks]: 28,
}
